import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Spinner, Modal } from 'react-bootstrap';
import { fetchOrgAuditLogs, fetchAuditLogFilters } from '../../actioncreators/actioncreators';
import {
  History,
  Filter,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  FileText,
  Clock,
  RefreshCw,
  Search,
  Building2,
  ArrowRight,
  Minus,
  Plus
} from 'lucide-react';
import './AuditLogs.css';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entityType: '',
    userAction: '',
    actorId: '',
    startDate: '',
    endDate: '',
    pageNumber: 1,
    pageSize: 20
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [entityTypes, setEntityTypes] = useState([]);
  const [userActions, setUserActions] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const POLL_INTERVAL_MS = 30_000; // 30 seconds
  const pollTimerRef = useRef(null);
  const filtersRef = useRef(filters);

  // Keep filtersRef current so the poll callback always uses the latest filters
  // without needing to be recreated on every render.
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  // Get Super Admin selected organization from Redux
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  // Get user role from Redux
  const loginResponse = useSelector((state) => state.loginResponse);
  const isSuperAdmin = loginResponse?.role_name?.toUpperCase() === 'SUPER_ADMIN' || loginResponse?.roleName?.toUpperCase() === 'SUPER_ADMIN';

  const superAdminSelectedOrgIdRef = useRef(superAdminSelectedOrgId);
  const isSuperAdminRef = useRef(isSuperAdmin);
  useEffect(() => { superAdminSelectedOrgIdRef.current = superAdminSelectedOrgId; }, [superAdminSelectedOrgId]);
  useEffect(() => { isSuperAdminRef.current = isSuperAdmin; }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin || superAdminSelectedOrgId) {
      fetchFilterOptions();
    }
  }, [superAdminSelectedOrgId]);

  useEffect(() => {
    if (!isSuperAdmin || superAdminSelectedOrgId) {
      fetchLogs();
    }
    // Reset the poll timer whenever filters/org change so we don't double-fetch.
    startPollTimer();
    return () => clearPollTimer();
  }, [filters, superAdminSelectedOrgId]);

  // Pause polling when the tab is hidden; resume and fetch immediately on visibility.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        clearPollTimer();
      } else {
        const sa = isSuperAdminRef.current;
        const orgId = superAdminSelectedOrgIdRef.current;
        if (!sa || orgId) fetchLogs();
        startPollTimer();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearPollTimer();
    };
  }, []);

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const startPollTimer = () => {
    clearPollTimer();
    pollTimerRef.current = setInterval(() => {
      if (!document.hidden) {
        const sa = isSuperAdminRef.current;
        const orgId = superAdminSelectedOrgIdRef.current;
        if (!sa || orgId) fetchLogsQuiet();
      }
    }, POLL_INTERVAL_MS);
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await fetchAuditLogFilters();
      if (response.data?.businessStatusCode === 2) {
        setEntityTypes(response.data.entityTypes || []);
        setUserActions(response.data.userActions || []);
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const applyLogsResponse = (data) => {
    setLogs(Array.isArray(data.auditLogs) ? data.auditLogs : []);
    const records = data.totalRecords || 0;
    const pages = data.totalPages || 0;
    setTotalRecords(records);
    setTotalPages(pages > 0 ? pages : records > 0 ? 1 : 0);
    setLastUpdated(new Date());
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const payload = { ...filtersRef.current };
      if (isSuperAdminRef.current && superAdminSelectedOrgIdRef.current) {
        payload.org_id = superAdminSelectedOrgIdRef.current;
      }
      const response = await fetchOrgAuditLogs(payload);
      if (response.data?.businessStatusCode === 2) {
        applyLogsResponse(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Silent background poll — no loading spinner, preserves current data while fetching.
  const fetchLogsQuiet = async () => {
    try {
      const payload = { ...filtersRef.current };
      if (isSuperAdminRef.current && superAdminSelectedOrgIdRef.current) {
        payload.org_id = superAdminSelectedOrgIdRef.current;
      }
      const response = await fetchOrgAuditLogs(payload);
      if (response.data?.businessStatusCode === 2) {
        applyLogsResponse(response.data);
      }
    } catch {
      // Silently ignore poll errors — user still sees the last successful data.
    }
  };

  const isValidDateYear = (value) => {
    if (!value) return true;
    const year = parseInt(value.split('-')[0], 10);
    return value.split('-')[0].length === 4 && year >= 1900 && year <= 2100;
  };

  const handleFilterChange = (field, value) => {
    if ((field === 'startDate' || field === 'endDate') && !isValidDateYear(value)) return;
    setFilters({ ...filters, [field]: value, pageNumber: 1 });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setFilters({ ...filters, pageNumber: newPage });
    }
  };

  const clearFilters = () => {
    setFilters({
      entityType: '',
      userAction: '',
      actorId: '',
      startDate: '',
      endDate: '',
      pageNumber: 1,
      pageSize: 20
    });
  };

  const hasActiveFilters = filters.entityType || filters.userAction || filters.startDate || filters.endDate;

  const getActionConfig = (action) => {
    const configs = {
      'CREATE': { color: '#10b981', bg: '#ecfdf5' },
      'UPDATE': { color: '#3b82f6', bg: '#eff6ff' },
      'DELETE': { color: '#ef4444', bg: '#fef2f2' },
      'ASSIGN': { color: '#8b5cf6', bg: '#f5f3ff' },
      'REMOVE': { color: '#f59e0b', bg: '#fffbeb' },
      'ACTIVE': { color: '#10b981', bg: '#ecfdf5' },
      'INACTIVE': { color: '#6b7280', bg: '#f3f4f6' }
    };
    return configs[action?.toUpperCase()] || { color: '#6b7280', bg: '#f3f4f6' };
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatEntityType = (entityType) => {
    if (!entityType) return 'N/A';
    return entityType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleViewDetails = (log, e) => {
    e.stopPropagation();
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  // Format field names to be more readable
  const formatFieldName = (key) => {
    if (!key) return '';
    // Convert snake_case to Title Case with spaces
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Format values for display - handles dates, booleans, objects, etc.
  const formatValue = (value, fieldName = '') => {
    if (value === null || value === undefined || value === '') {
      return <span className="audit-empty-value">-</span>;
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    // Format dates
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
      try {
        const date = new Date(value);
        return date.toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return String(value);
      }
    }
    // Format arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="audit-empty-value">-</span>;
      return value.map((item, idx) => (
        <span key={idx} className="audit-array-item">{typeof item === 'object' ? JSON.stringify(item) : String(item)}</span>
      ));
    }
    // Format objects
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  // Parse JSON data - handles arrays and objects
  const parseAuditData = (data) => {
    if (!data) return {};
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      // If it's an array, extract the first element or merge all elements
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) return {};
        if (parsed.length === 1) return parsed[0];
        // For multiple items, create a combined view
        return parsed.reduce((acc, item, idx) => {
          Object.keys(item).forEach(key => {
            if (acc[key] === undefined) {
              acc[key] = item[key];
            }
          });
          return acc;
        }, {});
      }
      return parsed;
    } catch {
      return {};
    }
  };

  // Fields to hide from display (internal IDs, etc.)
  const hiddenFields = ['org_id', 'created_by', 'updated_by', 'creation_date_and_time', 'updated_date_and_time'];

  // Fields that are IDs but should show with shortened display
  const idFields = ['user_id', 'assignment_id', 'work_schedule_id', 'location_id', 'manager_id', 'entity_id'];

  const renderChangesTable = (oldData, newData, action) => {
    try {
      const oldParsed = parseAuditData(oldData);
      const newParsed = parseAuditData(newData);

      // Get all unique keys from both objects
      const allKeys = [...new Set([...Object.keys(oldParsed), ...Object.keys(newParsed)])];

      if (allKeys.length === 0) {
        return <p className="audit-no-changes">No data available</p>;
      }

      // Filter and sort fields - hide internal fields and empty values
      const fieldsToShow = allKeys.filter(key => {
        // Hide internal fields
        if (hiddenFields.includes(key)) return false;

        const oldVal = oldParsed[key];
        const newVal = newParsed[key];
        // Skip if both are empty
        if ((oldVal === null || oldVal === undefined || oldVal === '') &&
            (newVal === null || newVal === undefined || newVal === '')) {
          return false;
        }
        return true;
      }).sort((a, b) => {
        // Sort: name fields first, then dates, then others, IDs last
        const priority = (key) => {
          if (key.includes('name')) return 0;
          if (key.includes('date')) return 1;
          if (key.includes('status')) return 2;
          if (idFields.includes(key)) return 4;
          return 3;
        };
        return priority(a) - priority(b);
      });

      if (fieldsToShow.length === 0) {
        return <p className="audit-no-changes">No data available</p>;
      }

      // For CREATE action, show only new data
      if (action === 'CREATE') {
        return (
          <div className="audit-changes-table">
            <div className="audit-changes-header two-col">
              <div className="audit-changes-col field">Field</div>
              <div className="audit-changes-col value">Value</div>
            </div>
            <div className="audit-changes-body">
              {fieldsToShow.map(key => (
                <div key={key} className="audit-changes-row created">
                  <div className="audit-changes-col field">
                    <Plus size={12} className="change-icon added" />
                    {formatFieldName(key)}
                  </div>
                  <div className="audit-changes-col value new">{formatValue(newParsed[key], key)}</div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      // For DELETE action, show only old data
      if (action === 'DELETE') {
        return (
          <div className="audit-changes-table">
            <div className="audit-changes-header two-col">
              <div className="audit-changes-col field">Field</div>
              <div className="audit-changes-col value">Value</div>
            </div>
            <div className="audit-changes-body">
              {fieldsToShow.map(key => (
                <div key={key} className="audit-changes-row deleted">
                  <div className="audit-changes-col field">
                    <Minus size={12} className="change-icon removed" />
                    {formatFieldName(key)}
                  </div>
                  <div className="audit-changes-col value old">{formatValue(oldParsed[key], key)}</div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      // For UPDATE and other actions, show comparison - only show changed fields
      const changedFields = fieldsToShow.filter(key => {
        const oldVal = oldParsed[key];
        const newVal = newParsed[key];
        return JSON.stringify(oldVal) !== JSON.stringify(newVal);
      });

      // If no fields changed, show all fields
      const displayFields = changedFields.length > 0 ? changedFields : fieldsToShow;

      return (
        <div className="audit-changes-table">
          <div className="audit-changes-header">
            <div className="audit-changes-col field">Field</div>
            <div className="audit-changes-col value">Old Value</div>
            <div className="audit-changes-col arrow"></div>
            <div className="audit-changes-col value">New Value</div>
          </div>
          <div className="audit-changes-body">
            {displayFields.map(key => {
              const oldVal = oldParsed[key];
              const newVal = newParsed[key];
              const hasChanged = JSON.stringify(oldVal) !== JSON.stringify(newVal);

              return (
                <div key={key} className={`audit-changes-row ${hasChanged ? 'changed' : ''}`}>
                  <div className="audit-changes-col field">
                    {formatFieldName(key)}
                  </div>
                  <div className={`audit-changes-col value old ${hasChanged ? 'highlight' : ''}`}>
                    {formatValue(oldVal, key)}
                  </div>
                  <div className="audit-changes-col arrow">
                    {hasChanged && <ArrowRight size={14} className="arrow-icon" />}
                  </div>
                  <div className={`audit-changes-col value new ${hasChanged ? 'highlight' : ''}`}>
                    {formatValue(newVal, key)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    } catch (error) {
      console.error('Error parsing audit data:', error);
      return (
        <div className="audit-raw-data-container">
          {oldData && <pre className="audit-raw-data"><strong>Old:</strong> {typeof oldData === 'string' ? oldData : JSON.stringify(oldData, null, 2)}</pre>}
          {newData && <pre className="audit-raw-data"><strong>New:</strong> {typeof newData === 'string' ? newData : JSON.stringify(newData, null, 2)}</pre>}
        </div>
      );
    }
  };

  // Show message for Super Admin to select an organization
  if (isSuperAdmin && !superAdminSelectedOrgId) {
    return (
      <div className="audit-page">
        <div className="audit-header">
          <div className="audit-header-left">
            <div className="audit-header-icon">
              <History size={20} />
            </div>
            <div>
              <h1>Audit Logs</h1>
              <p>Super Admin View</p>
            </div>
          </div>
        </div>
        <div className="audit-select-org">
          <Building2 size={48} strokeWidth={1.5} />
          <h3>Select an Organization</h3>
          <p>Please select an organization from the header dropdown to view its audit logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-page">
      {/* Header */}
      <div className="audit-header">
        <div className="audit-header-left">
          <div className="audit-header-icon">
            <History size={20} />
          </div>
          <div>
            <h1>Audit Logs</h1>
            <p>Track all changes and actions in your organization</p>
          </div>
        </div>
        <div className="audit-header-right">
          <button
            className={`audit-filter-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            title="Toggle Filters"
          >
            <Filter size={16} />
            <span>Filters</span>
            {hasActiveFilters && <span className="filter-count">{[filters.entityType, filters.userAction, filters.startDate, filters.endDate].filter(Boolean).length}</span>}
          </button>
          {lastUpdated && !loading && (
            <span style={{ fontSize: '11px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
              Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button className="audit-refresh-btn" onClick={fetchLogs} disabled={loading} title="Refresh now">
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="audit-filters">
          <div className="audit-filter-item">
            <label><FileText size={12} /> Entity</label>
            <select value={filters.entityType} onChange={(e) => handleFilterChange('entityType', e.target.value)}>
              <option value="">All</option>
              {entityTypes.map((type) => (
                <option key={type} value={type}>{formatEntityType(type)}</option>
              ))}
            </select>
          </div>
          <div className="audit-filter-item">
            <label>Action</label>
            <select value={filters.userAction} onChange={(e) => handleFilterChange('userAction', e.target.value)}>
              <option value="">All</option>
              {userActions.map((action) => (
                <option key={action} value={action}>{action}</option>
              ))}
            </select>
          </div>
          <div className="audit-filter-item">
            <label><Calendar size={12} /> From</label>
            <input
              type="date"
              value={filters.startDate}
              min="1900-01-01"
              max="2100-12-31"
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>
          <div className="audit-filter-item">
            <label><Calendar size={12} /> To</label>
            <input
              type="date"
              value={filters.endDate}
              min="1900-01-01"
              max="2100-12-31"
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>
          {hasActiveFilters && (
            <button className="audit-clear-btn" onClick={clearFilters}>
              <X size={12} /> Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="audit-table-container">
        {/* Table Header */}
        <div className="audit-table-header">
          <div className="audit-col-datetime">Date & Time</div>
          <div className="audit-col-action">Action</div>
          <div className="audit-col-entity">Entity</div>
          <div className="audit-col-user">User</div>
          <div className="audit-col-remarks">Remarks</div>
          <div className="audit-col-details">Details</div>
        </div>

        {/* Table Body */}
        <div className="audit-table-body">
          {loading ? (
            <div className="audit-loading">
              <Spinner animation="border" size="sm" />
              <span>Loading...</span>
            </div>
          ) : logs.length > 0 ? (
            logs.map((log, index) => {
              const actionConfig = getActionConfig(log.user_action);
              return (
                <div key={log.ref_data_audit_id || index} className="audit-row">
                  <div className="audit-col-datetime">
                    <Clock size={12} />
                    {formatDateTime(log.date_and_time)}
                  </div>
                  <div className="audit-col-action">
                    <span className="audit-action-badge" style={{ backgroundColor: actionConfig.bg, color: actionConfig.color }}>
                      {log.user_action}
                    </span>
                  </div>
                  <div className="audit-col-entity">
                    <span className="entity-type-label">{formatEntityType(log.entity_type)}</span>
                    {log.entity_name && (
                      <span className="entity-name-label" title={log.entity_name}>
                        {log.entity_name}
                      </span>
                    )}
                  </div>
                  <div className="audit-col-user">
                    <div className="audit-user-avatar">{(log.user_name || 'U')[0].toUpperCase()}</div>
                    <div className="audit-user-info">
                      <span className="audit-user-name">{log.user_name || 'Unknown'}</span>
                      {log.user_email && (
                        <span className="audit-user-email">{log.user_email}</span>
                      )}
                    </div>
                  </div>
                  <div className="audit-col-remarks">
                    {log.remarks ? (
                      <span title={log.remarks}>{log.remarks.length > 30 ? log.remarks.substring(0, 30) + '...' : log.remarks}</span>
                    ) : (
                      <span className="no-remarks">-</span>
                    )}
                  </div>
                  <div className="audit-col-details">
                    <button className="audit-view-btn" onClick={(e) => handleViewDetails(log, e)} title="View Details">
                      <Eye size={16} strokeWidth={2} />
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="audit-empty">
              <Search size={32} />
              <span>No audit logs found</span>
              {hasActiveFilters && <button onClick={clearFilters}>Clear Filters</button>}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && logs.length > 0 && (
          <div className="audit-table-footer">
            <span className="audit-record-info">
              Showing {totalRecords === 0 ? 0 : ((filters.pageNumber - 1) * filters.pageSize) + 1} – {Math.min(filters.pageNumber * filters.pageSize, totalRecords)} of {totalRecords}
            </span>
            <div className="audit-pagination">
              <button
                disabled={filters.pageNumber <= 1}
                onClick={() => handlePageChange(filters.pageNumber - 1)}
              >
                <ChevronLeft size={16} />
              </button>
              <span>{filters.pageNumber} / {totalPages || 1}</span>
              <button
                disabled={filters.pageNumber >= totalPages}
                onClick={() => handlePageChange(filters.pageNumber + 1)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered className="audit-modal">
        <Modal.Body className="p-0">
          {selectedLog && (
            <div className="audit-detail">
              <div className="audit-detail-header">
                <div className="audit-detail-title">
                  <span className="audit-action-badge large" style={{ backgroundColor: getActionConfig(selectedLog.user_action).bg, color: getActionConfig(selectedLog.user_action).color }}>
                    {selectedLog.user_action}
                  </span>
                  <span>{formatEntityType(selectedLog.entity_type)}</span>
                </div>
                <button className="audit-close-btn" onClick={() => setShowDetailModal(false)} title="Close">
                  <X size={20} />
                </button>
              </div>
              <div className="audit-detail-body">
                <div className="audit-detail-row">
                  <div className="audit-detail-item">
                    <label>Date & Time</label>
                    <span>{formatDateTime(selectedLog.date_and_time)}</span>
                  </div>
                  <div className="audit-detail-item">
                    <label>User</label>
                    <span>{selectedLog.user_name || 'Unknown'} ({selectedLog.user_email || 'N/A'})</span>
                  </div>
                </div>
                {selectedLog.entity_id && (
                  <div className="audit-detail-item full">
                    <label>Entity ID</label>
                    <div className="entity-id-display">
                      <code>{selectedLog.entity_id}</code>
                      {selectedLog.entity_name && (
                        <span className="entity-name-badge">{selectedLog.entity_name}</span>
                      )}
                    </div>
                  </div>
                )}
                {selectedLog.remarks && (
                  <div className="audit-detail-item full">
                    <label>Remarks</label>
                    <p>{selectedLog.remarks}</p>
                  </div>
                )}
                <div className="audit-detail-changes">
                  <h6 className="audit-changes-title">
                    {selectedLog.user_action === 'CREATE' ? 'Created Data' :
                     selectedLog.user_action === 'DELETE' ? 'Deleted Data' : 'Changes'}
                  </h6>
                  {renderChangesTable(selectedLog.old_data, selectedLog.new_data, selectedLog.user_action)}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AuditLogs;
